import { useEffect, useRef, useState } from 'react';

/**
 * This hook peforms similarly to `useClickAway` from `react-use` but it also listens for the `Escape` keydown event.
 */
export const useClickAwayListener = () => {
  const mouseEventRef = useRef<HTMLDivElement>(null);
  const [hasClickedAway, setHasClickedAway] = useState(false);
  const forceClickAwayEvent = new Event('forceClickAway');

  const handleClickAway = (event: MouseEvent) => {
    if (mouseEventRef.current && !mouseEventRef.current.contains(event.target as Node)) {
      setHasClickedAway(true);
    }
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setHasClickedAway(true);
    }
  };

  /**
   * To be used only if the `mouseup` event does not fire to trigger `handleClickAway`.
   */
  const onForceClickAway = (event: any) => {
    setHasClickedAway(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    document.addEventListener('mouseup', handleClickAway);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('forceClickAway', onForceClickAway);

    return () => {
      document.removeEventListener('mouseup', handleClickAway);
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('forceClickAway', onForceClickAway);
    };
  }, []);

  // reset hasClickedAway
  useEffect(() => {
    if (hasClickedAway) {
      setHasClickedAway(false);
    }
  }, [hasClickedAway]);

  return {
    mouseEventRef,
    hasClickedAway,
    forceClickAwayEvent,
  };
};
